import type { ChatChapter, ChatMessage } from '../chat/chat-types';

/**
 * The signup pitch. Hardcoded on purpose: unlike the login panel it is not
 * something a Super Admin edits, it is the product's standing sales answer, and it
 * changes with the pricing and the trial terms rather than week to week.
 *
 * There is deliberately no "What do customers say?" question. The design drew one
 * with a placeholder quote from "Sample customer", and a made-up testimonial on a
 * live signup page is not something to ship as a stand-in. Add the question back
 * when there is an approved quote to put behind it.
 *
 * Confirmed: the trial terms (12 days, a credit card is required to start, onboarding
 * support is free) and the 27% average uplift figure. The footer line under the
 * "Create your account" button repeats the trial terms (SignupChat.tsx), so change them
 * together.
 *
 * The pitch sells the outcome, not the feature list: more revenue from the traffic
 * people already have, reached by understanding visitors, testing what works and
 * learning from every result. Soft values sell, and they keep it true as the product
 * grows past A/B testing, which a pitch built on "experiments" would not. The product
 * vocabulary (CRO, A/B tests, surveys, monitoring) is in the answer to "How does
 * Pertento do that?", for the visitor who asks, and names only what the console shows
 * today. Personalizations is in the code but not in the sidebar, so it is left out
 * until it is.
 */
export const SIGNUP_CHAPTER: ChatChapter = {
  messages: [
    { kind: 'text', from: 'pt', text: 'Hey, welcome to Pertento.' },
    { kind: 'headline', text: 'Get more revenue from the traffic you already have.' },
    {
      kind: 'text',
      from: 'pt',
      text: 'Pertento helps you generate more revenue from your existing traffic by understanding your visitors, testing what works and learning from every result.',
    },
    {
      kind: 'text',
      from: 'pt',
      text: 'Customers see a 27% average conversion uplift. And it respects your visitors: the tracker is 0.9 KB, GDPR and CCPA compliant, and cookie-free by design.',
    },
  ],
  questions: [
    {
      question: 'How does Pertento do that?',
      answer: [
        {
          kind: 'text',
          from: 'pt',
          text: 'Pertento is a website optimization platform, also known as conversion rate optimization (CRO). Test ideas with A/B experiments and read the results in plain English, hear from your visitors with surveys, and keep an eye on your site for problems. Everything you learn stays together in one library.',
        },
      ],
    },
    {
      question: 'How does the trial work?',
      answer: [
        {
          kind: 'text',
          from: 'pt',
          text: 'You get 12 days with everything included. After you confirm your email you choose a plan and add a card, and nothing is charged until the trial ends. Onboarding support is free.',
        },
      ],
    },
    {
      question: 'How much setup is there?',
      answer: [
        {
          kind: 'text',
          from: 'pt',
          text: 'Very little. Add one script tag to your website, then make your first changes in the visual editor without touching your code.',
        },
      ],
    },
  ],
};

export const CREATE_ACCOUNT_LABEL = 'Create my account';

/**
 * Said after "Create my account", just before the form card appears. The consent
 * line comes before the form on purpose: the account is created at the end of the
 * conversation, so it has to be said before anything is submitted, not after.
 */
export const FORM_INTRO_MESSAGES: readonly ChatMessage[] = [
  { kind: 'text', from: 'pt', text: 'Great. Start with the basics.' },
  { kind: 'consent' },
];

export const TYPE_QUESTION_LINE = 'It decides how your workspace is set up.';
